import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiGlobe } from 'react-icons/fi';
import Layout from '../../Layout/Layout';
import styles from './EmailManagement.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * EmailManagement - Email Account Management for Super Admin
 * Complete CRUD operations for managing email accounts
 * Design follows DNS Records page pattern
 */
function EmailManagement() {
    const [accounts, setAccounts] = useState([]);
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState('list');

    // Domain search state
    const [domainSearch, setDomainSearch] = useState('');
    const [showDomainDropdown, setShowDomainDropdown] = useState(false);
    const [filteredDomains, setFilteredDomains] = useState([]);
    const domainDropdownRef = useRef(null);

    const [formData, setFormData] = useState({
        domainId: '',
        domain: '',
        username: '',
        displayName: '',
        password: '',
        confirmPassword: '',
        quota: 1024,
        isActive: true,
        forwardTo: '',
        accountType: 'user',
        autoReply: {
            enabled: false,
            subject: '',
            message: '',
            startDate: '',
            endDate: ''
        },
        notes: ''
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 10,
        hasPrevPage: false,
        hasNextPage: false
    });

    const [filters, setFilters] = useState({
        search: '',
        domain: '',
        isActive: 'all'
    });

    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    // Click outside handler to close domain dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (domainDropdownRef.current && !domainDropdownRef.current.contains(event.target)) {
                setShowDomainDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter domains based on search
    useEffect(() => {
        if (domainSearch.trim()) {
            const filtered = domains.filter(d =>
                d.domain.toLowerCase().includes(domainSearch.toLowerCase()) ||
                (d.store && d.store.toLowerCase().includes(domainSearch.toLowerCase()))
            );
            setFilteredDomains(filtered);
        } else {
            setFilteredDomains(domains);
        }
    }, [domainSearch, domains]);

    useEffect(() => {
        fetchAccounts();
    }, [pagination.currentPage, filters]);

    useEffect(() => {
        fetchDomains();
    }, []);

    const fetchDomains = async () => {
        try {
            const response = await axios.get(
                `${BASEURL}superadmin/email-domains/${userId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setDomains(response.data.data.domains || []);
                setFilteredDomains(response.data.data.domains || []);
            }
        } catch (err) {
            console.error('Error fetching domains:', err);
        }
    };

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            setError('');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.isActive !== 'all' && { isActive: filters.isActive }),
                ...(filters.domain && { domain: filters.domain }),
                ...(filters.search && { search: filters.search })
            });

            const response = await axios.get(
                `${BASEURL}superadmin/email-accounts/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setAccounts(response.data.data.accounts);
                setPagination(response.data.data.pagination);
            } else {
                setError(response.data.error || 'Failed to load email accounts');
            }
        } catch (err) {
            console.error('Error fetching email accounts:', err);
            setError(err.response?.data?.error || 'Failed to load email accounts');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (account = null) => {
        if (account) {
            setEditingAccount(account);
            setFormData({
                domainId: account.domainId,
                domain: account.domain,
                username: account.username,
                displayName: account.displayName || '',
                password: '',
                confirmPassword: '',
                quota: account.quota || 1024,
                isActive: account.isActive,
                forwardTo: account.forwardTo || '',
                accountType: account.accountType || 'user',
                autoReply: {
                    enabled: account.autoReply?.enabled || false,
                    subject: account.autoReply?.subject || '',
                    message: account.autoReply?.message || '',
                    startDate: account.autoReply?.startDate ? account.autoReply.startDate.split('T')[0] : '',
                    endDate: account.autoReply?.endDate ? account.autoReply.endDate.split('T')[0] : ''
                },
                notes: account.notes || ''
            });
            setDomainSearch(account.domain);
        } else {
            setEditingAccount(null);
            setFormData({
                domainId: '',
                domain: '',
                username: '',
                displayName: '',
                password: '',
                confirmPassword: '',
                quota: 1024,
                isActive: true,
                forwardTo: '',
                accountType: 'user',
                autoReply: {
                    enabled: false,
                    subject: '',
                    message: '',
                    startDate: '',
                    endDate: ''
                },
                notes: ''
            });
            setDomainSearch('');
        }
        setActiveTab('basic');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingAccount(null);
        setActiveTab('basic');
        setDomainSearch('');
        setShowDomainDropdown(false);
    };

    const handleDomainSelect = (domain) => {
        setFormData({
            ...formData,
            domainId: domain._id || 'root-domain',
            domain: domain.domain
        });
        setDomainSearch(domain.domain);
        setShowDomainDropdown(false);
    };

    const handleSubmit = async () => {
        try {
            // Validate required fields
            if (!editingAccount) {
                if (!formData.domainId) {
                    alert('Please select a domain');
                    return;
                }

                if (!formData.username || !formData.username.trim()) {
                    alert('Username is required');
                    return;
                }

                if (!formData.password || formData.password.length < 6) {
                    alert('Password must be at least 6 characters');
                    return;
                }

                if (formData.password !== formData.confirmPassword) {
                    alert('Passwords do not match');
                    return;
                }
            } else {
                // For updates, only validate password if provided
                if (formData.password && formData.password.length < 6) {
                    alert('Password must be at least 6 characters');
                    return;
                }

                if (formData.password && formData.password !== formData.confirmPassword) {
                    alert('Passwords do not match');
                    return;
                }
            }

            setSubmitting(true);

            const url = editingAccount
                ? `${BASEURL}superadmin/email-account/${userId}/${editingAccount._id}`
                : `${BASEURL}superadmin/email-account/${userId}`;

            const method = editingAccount ? 'put' : 'post';

            // Prepare data (exclude confirmPassword)
            const submitData = { ...formData };
            delete submitData.confirmPassword;

            // Don't send empty password on update
            if (editingAccount && !submitData.password) {
                delete submitData.password;
            }

            const response = await axios[method](url, submitData, { headers: { token } });

            if (response.data.success) {
                closeModal();
                fetchAccounts();
                alert(editingAccount ? 'Email account updated successfully!' : 'Email account created successfully!');
            } else {
                alert(response.data.error || 'Failed to save email account');
            }
        } catch (err) {
            console.error('Error saving email account:', err);
            alert(err.response?.data?.error || 'Failed to save email account');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (accountId, email) => {
        if (!window.confirm(`Are you sure you want to delete email account "${email}"?`)) {
            return;
        }

        try {
            const response = await axios.delete(
                `${BASEURL}superadmin/email-account/${userId}/${accountId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                fetchAccounts();
            } else {
                alert(response.data.error || 'Failed to delete email account');
            }
        } catch (err) {
            console.error('Error deleting email account:', err);
            alert(err.response?.data?.error || 'Failed to delete email account');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getQuotaPercentage = (used, total) => {
        if (!total) return 0;
        return Math.min((used / total) * 100, 100);
    };

    const formatQuota = (mb) => {
        if (mb >= 1024) {
            return `${(mb / 1024).toFixed(1)} GB`;
        }
        return `${mb} MB`;
    };

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            {/* Root Domain - Searchable Dropdown */}
            <div className={styles.formGroup} ref={domainDropdownRef}>
                <label>Root Domain *</label>
                <div className={styles.searchableSelect}>
                    <input
                        type="text"
                        value={domainSearch}
                        onChange={(e) => {
                            setDomainSearch(e.target.value);
                            setShowDomainDropdown(true);
                            // Clear selection if user types
                            if (formData.domain !== e.target.value) {
                                setFormData({ ...formData, domainId: '', domain: '' });
                            }
                        }}
                        onFocus={() => setShowDomainDropdown(true)}
                        placeholder="Search and select domain (e.g., rusubd.com)"
                        disabled={!!editingAccount}
                        className={formData.domain ? styles.selectedInput : ''}
                    />
                    {formData.domain && !editingAccount && (
                        <button
                            type="button"
                            className={styles.clearButton}
                            onClick={() => {
                                setFormData({ ...formData, domainId: '', domain: '' });
                                setDomainSearch('');
                            }}
                        >
                            &times;
                        </button>
                    )}
                    {showDomainDropdown && !editingAccount && (
                        <div className={styles.dropdownList}>
                            {filteredDomains.length > 0 ? (
                                filteredDomains.map((domain, index) => (
                                    <div
                                        key={domain._id || `domain-${index}`}
                                        className={`${styles.dropdownItem} ${formData.domainId === domain._id || (formData.domain === domain.domain && domain.isRootDomain) ? styles.selected : ''}`}
                                        onClick={() => handleDomainSelect(domain)}
                                    >
                                        <strong>{domain.domain}</strong>
                                        {domain.store && <span className={styles.storeName}> ({domain.store})</span>}
                                        {domain.isRootDomain && <span className={styles.rootBadge}> <FiGlobe style={{ verticalAlign: 'middle' }} /></span>}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.noResults}>
                                    {domainSearch ? 'No domains found matching your search' : 'No domains available'}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {formData.domain && (
                    <div className={styles.selectedDomain}>
                        Selected: <strong>{formData.domain}</strong>
                    </div>
                )}
            </div>

            <div className={styles.formGroup}>
                <label>Username *</label>
                <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                    placeholder="username"
                    disabled={!!editingAccount}
                />
                {formData.domain && formData.username && (
                    <div className={styles.emailPreview}>
                        Email: {formData.username}@{formData.domain}
                    </div>
                )}
            </div>

            <div className={styles.formGroup}>
                <label>Display Name</label>
                <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="John Doe"
                />
            </div>

            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>{editingAccount ? 'New Password (leave empty to keep current)' : 'Password *'}</label>
                    <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={editingAccount ? '••••••••' : 'Enter password'}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Confirm Password</label>
                    <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm password"
                    />
                </div>
            </div>

            <div className={styles.formGroup}>
                <label>Account Type</label>
                <select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="alias">Alias</option>
                    <option value="catchall">Catch-All</option>
                </select>
            </div>

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                </label>
            </div>
        </div>
    );

    const renderSettingsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>Mailbox Quota (MB)</label>
                <input
                    type="number"
                    value={formData.quota}
                    onChange={(e) => setFormData({ ...formData, quota: parseInt(e.target.value) || 1024 })}
                    min="100"
                    max="102400"
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Current: {formatQuota(formData.quota)} | Max recommended: 100 GB
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>Forward Emails To</label>
                <input
                    type="email"
                    value={formData.forwardTo}
                    onChange={(e) => setFormData({ ...formData, forwardTo: e.target.value })}
                    placeholder="forward@example.com"
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Leave empty to disable forwarding
                </p>
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            <h3 style={{ marginBottom: '16px', color: '#202223' }}>Auto-Reply Settings</h3>

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.autoReply.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            autoReply: { ...formData.autoReply, enabled: e.target.checked }
                        })}
                    />
                    Enable Auto-Reply
                </label>
            </div>

            {formData.autoReply.enabled && (
                <>
                    <div className={styles.formGroup}>
                        <label>Subject</label>
                        <input
                            type="text"
                            value={formData.autoReply.subject}
                            onChange={(e) => setFormData({
                                ...formData,
                                autoReply: { ...formData.autoReply, subject: e.target.value }
                            })}
                            placeholder="Out of Office"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Message</label>
                        <textarea
                            value={formData.autoReply.message}
                            onChange={(e) => setFormData({
                                ...formData,
                                autoReply: { ...formData.autoReply, message: e.target.value }
                            })}
                            placeholder="Thank you for your email. I am currently out of the office..."
                            rows="4"
                        />
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Start Date</label>
                            <input
                                type="date"
                                value={formData.autoReply.startDate}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    autoReply: { ...formData.autoReply, startDate: e.target.value }
                                })}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>End Date</label>
                            <input
                                type="date"
                                value={formData.autoReply.endDate}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    autoReply: { ...formData.autoReply, endDate: e.target.value }
                                })}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    const renderNotesTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>Admin Notes</label>
                <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Internal notes about this email account..."
                    rows="6"
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    These notes are only visible to administrators
                </p>
            </div>

            {editingAccount && (
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '5px',
                    marginTop: '20px',
                    border: '1px solid #dee2e6'
                }}>
                    <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#495057' }}>
                        Account Information
                    </h4>
                    <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                        <p><strong>Created:</strong> {new Date(editingAccount.createdAt).toLocaleString()}</p>
                        <p><strong>Last Updated:</strong> {new Date(editingAccount.updatedAt).toLocaleString()}</p>
                        {editingAccount.lastLogin && (
                            <p><strong>Last Login:</strong> {new Date(editingAccount.lastLogin).toLocaleString()}</p>
                        )}
                        <p><strong>Storage Used:</strong> {formatQuota(editingAccount.usedStorage || 0)} / {formatQuota(editingAccount.quota || 1024)}</p>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1>Email Management</h1>
                        <p>Create and manage email accounts for domains</p>
                    </div>
                    <div className={styles.headerActions}>
                        <div className={styles.viewToggle}>
                            <button
                                className={`${styles.viewButton} ${viewMode === 'list' ? styles.activeView : ''}`}
                                onClick={() => setViewMode('list')}
                                title="List View"
                            >
                                &#9776;
                            </button>
                            <button
                                className={`${styles.viewButton} ${viewMode === 'grid' ? styles.activeView : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Grid View"
                            >
                                &#8862;
                            </button>
                        </div>
                        <button className={styles.addButton} onClick={() => openModal()}>
                            + Create Email Account
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className={styles.filters}>
                    <input
                        type="text"
                        placeholder="Search by email, username, or domain..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className={styles.searchInput}
                    />
                    <select
                        value={filters.domain}
                        onChange={(e) => handleFilterChange('domain', e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Domains</option>
                        {domains.map((domain) => (
                            <option key={domain._id} value={domain.domain}>
                                {domain.domain}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filters.isActive}
                        onChange={(e) => handleFilterChange('isActive', e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="all">All Status</option>
                        <option value="true">Active Only</option>
                        <option value="false">Inactive Only</option>
                    </select>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {loading ? (
                    <div className={styles.loading}>Loading email accounts...</div>
                ) : accounts.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No email accounts found</p>
                        <p className={styles.emptyHint}>Click "Create Email Account" to create your first email account.</p>
                        <button onClick={() => openModal()}>Create First Account</button>
                    </div>
                ) : (
                    <>
                        {/* List View (Table) */}
                        {viewMode === 'list' && (
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Display Name</th>
                                            <th>Type</th>
                                            <th>Quota</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accounts.map((account) => (
                                            <tr key={account._id}>
                                                <td>
                                                    <strong>{account.email}</strong>
                                                </td>
                                                <td>{account.displayName || '-'}</td>
                                                <td>
                                                    <span className={styles.typeBadge}>
                                                        {account.accountType}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className={styles.quotaInfo}>
                                                        <div className={styles.quotaBar}>
                                                            <div
                                                                className={styles.quotaFill}
                                                                style={{ width: `${getQuotaPercentage(account.usedStorage || 0, account.quota)}%` }}
                                                            />
                                                        </div>
                                                        <span className={styles.quotaText}>
                                                            {formatQuota(account.usedStorage || 0)} / {formatQuota(account.quota)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={account.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                        {account.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{new Date(account.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <div className={styles.tableActions}>
                                                        <button
                                                            className={styles.editButton}
                                                            onClick={() => openModal(account)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={() => handleDelete(account._id, account.email)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Grid View (Cards) */}
                        {viewMode === 'grid' && (
                            <div className={styles.configGrid}>
                                {accounts.map((account) => (
                                    <div key={account._id} className={styles.configCard}>
                                        <div className={styles.configHeader}>
                                            <div>
                                                <h3>{account.email}</h3>
                                                <span className={styles.domainMeta}>
                                                    {account.displayName || 'No display name'} - {account.accountType}
                                                </span>
                                            </div>
                                            <span className={account.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                {account.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>

                                        <div className={styles.configDetails}>
                                            <div className={styles.detailRow}>
                                                <strong>Domain:</strong>
                                                <span>{account.domain}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <strong>Storage:</strong>
                                                <span>{formatQuota(account.usedStorage || 0)} / {formatQuota(account.quota)}</span>
                                            </div>
                                            {account.forwardTo && (
                                                <div className={styles.detailRow}>
                                                    <strong>Forward To:</strong>
                                                    <span>{account.forwardTo}</span>
                                                </div>
                                            )}
                                            <div className={styles.detailRow}>
                                                <strong>Created:</strong>
                                                <span>{new Date(account.createdAt).toLocaleDateString()}</span>
                                            </div>
                                        </div>

                                        <div className={styles.configActions}>
                                            <button
                                                className={styles.editButton}
                                                onClick={() => openModal(account)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className={styles.deleteButton}
                                                onClick={() => handleDelete(account._id, account.email)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    disabled={!pagination.hasPrevPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} accounts)
                                </span>
                                <button
                                    disabled={!pagination.hasNextPage}
                                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
                        <div className={styles.modal}>
                            <div className={styles.modalHeader}>
                                <h2>{editingAccount ? 'Edit Email Account' : 'Create Email Account'}</h2>
                                <button className={styles.closeButton} onClick={closeModal}>&times;</button>
                            </div>

                            <div className={styles.tabs}>
                                <button
                                    className={activeTab === 'basic' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    Basic Info
                                </button>
                                <button
                                    className={activeTab === 'settings' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('settings')}
                                >
                                    Settings
                                </button>
                                <button
                                    className={activeTab === 'notes' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('notes')}
                                >
                                    Notes
                                </button>
                            </div>

                            <form style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                {activeTab === 'basic' && renderBasicTab()}
                                {activeTab === 'settings' && renderSettingsTab()}
                                {activeTab === 'notes' && renderNotesTab()}
                            </form>

                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.submitButton}
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Saving...' : (editingAccount ? 'Update Account' : 'Create Account')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default EmailManagement;
